import { useRef } from 'react'

import { useApolloClient } from '@apollo/client/react/hooks/useApolloClient'
import {
  DateRange,
  FiltersDefinition,
  FiltersState,
  OneDataCollection,
  useDataCollectionSource,
} from '@factorialco/f0-react/dist/experimental'
import { AnalyticsSnapshotResultsDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import {
  datatableFiltersToJsonLogicFilters,
  filterDefinitionsFromSchema,
} from 'modules/ai_reports/components/DatasourceTypeRenderer/filters'
import {
  applyColumnOrder,
  formatColumns,
} from 'modules/ai_reports/components/DatasourceTypeRenderer/utils'
import { useLatestRef } from 'modules/ai_reports/hooks/useLatestRef'
import { ReportSchemaType } from 'modules/ai_reports/lib/types'

import type { ReportTableAction, ReportTableProps } from './index'

interface SnapshotTableContentProps {
  id: string
  sqlQuery: string
  tableId: string
  actions?: {
    primary?: ReportTableAction
    secondary?: ReportTableAction[]
  }
  columnFormatHints: ReportTableProps['columnFormatHints']
  columnOrder: string[] | null
  onStateChange: ReportTableProps['onStateChange']
  dateFilter?: DateRange
  onUserFacingSqlQueryChange: (sqlQuery: string) => void
  currentFilters: FiltersState<FiltersDefinition<string>> | undefined
  currentSchema: ReportSchemaType
  onSchemaChanged: (schema: ReportSchemaType) => void
}

const INITIAL_PAGE = 1
const INITIAL_PAGE_SIZE = 10

const SnapshotTableContent = ({
  id,
  sqlQuery,
  tableId,
  actions,
  columnFormatHints,
  columnOrder,
  onStateChange,
  dateFilter,
  onUserFacingSqlQueryChange,
  currentFilters,
  currentSchema,
  onSchemaChanged,
}: SnapshotTableContentProps) => {
  const client = useApolloClient()
  const { company, access } = useCurrentSession()

  // Latest-value refs so fetchData always sees fresh schema/dateFilter without
  // having to put them in useDataCollectionSource deps (which would recreate the
  // source on every change and cancel in-flight queries → infinite loop).
  const schemaRef = useLatestRef(currentSchema)
  const dateFilterRef = useLatestRef(dateFilter)
  const onSchemaChangedRef = useLatestRef(onSchemaChanged)
  const onUserFacingSqlQueryChangeRef = useLatestRef(onUserFacingSqlQueryChange)

  // Fallback snapshotDate captured at mount time. Used when `dateFilter` is
  // undefined so we never hand fetchData a fresh `new Date()` on each call
  // (which would make identical queries look different and hide caching).
  const fallbackSnapshotDateRef = useRef(new Date())

  const source = useDataCollectionSource(
    {
      filters: filterDefinitionsFromSchema(currentSchema),
      currentFilters: currentFilters,
      primaryActions: () => actions?.primary,
      secondaryActions: actions?.secondary
        ? {
            expanded: 1,
            actions: () => actions?.secondary ?? [],
          }
        : undefined,
      dataAdapter: {
        paginationType: 'pages',
        perPage: INITIAL_PAGE_SIZE,
        fetchData: (options) => {
          const pagination = options.pagination
          const perPage = pagination.perPage ?? INITIAL_PAGE_SIZE
          const currentPage = pagination.currentPage ?? INITIAL_PAGE

          const snapshotDate = dateFilterRef.current?.from ?? fallbackSnapshotDateRef.current

          const filters = options.filters
          const jsonLogicFilters = datatableFiltersToJsonLogicFilters(filters, schemaRef.current)

          return client
            .watchQuery({
              query: AnalyticsSnapshotResultsDocument,
              fetchPolicy: 'cache-and-network',
              variables: {
                query: sqlQuery,
                companyId: company.id,
                snapshotDate: snapshotDate.toISOString(),
                page: currentPage,
                perPage,
                tableId,
                filters: JSON.stringify(jsonLogicFilters),
              },
              notifyOnNetworkStatusChange: false,
              nextFetchPolicy: 'cache-and-network',
            })
            .map(({ data, loading, error }) => {
              // Only propagate sqlQuery / schema updates when we actually have data.
              // Emitting empty arrays during loading states clobbers the schema state.
              const firstNode = data?.iris?.snapshotResultsConnection?.nodes?.[0]
              if (firstNode) {
                onUserFacingSqlQueryChangeRef.current(firstNode.sqlQuery ?? '')
                onSchemaChangedRef.current(firstNode.schema ?? [])
              }
              return {
                loading: loading,
                error: error,
                data: {
                  records:
                    data?.iris.snapshotResultsConnection.nodes.map((node) =>
                      Object.fromEntries(node.values.map(({ name, value }) => [name, value ?? '']))
                    ) ?? [],
                  total: data?.iris.snapshotResultsConnection.totalCount ?? 0,
                  currentPage: currentPage,
                  pagesCount: Math.ceil(
                    (data?.iris.snapshotResultsConnection.totalCount ?? 0) / perPage
                  ),
                  perPage: perPage,
                  type: 'pages',
                },
              }
            })
        },
      },
    },
    // `dateFilter` is read via a ref + timestamp dep (never a fresh reference
    // each render). `currentSchema` IS part of the identity because
    // `filterDefinitionsFromSchema(currentSchema)` is consumed at config build
    // time by the data collection — it needs to rebuild when the schema
    // materialises so the filter bar reflects the actual columns.
    // `useReportSchema` guards with `isEqual`, so `currentSchema` only changes
    // reference when the content changes, not on every render.
    [sqlQuery, company.id, dateFilter?.from?.getTime(), currentSchema]
  )

  return (
    <OneDataCollection
      key={`snapshot-${id}-${columnOrder?.length ?? 'default'}`}
      fullHeight
      source={source}
      id={`snapshot-table-${id}/v1`}
      storage={false}
      onStateChange={onStateChange}
      visualizations={[
        {
          type: 'table',
          options: {
            allowColumnReordering: true,
            allowColumnHiding: true,
            frozenColumns: 1,
            columns: applyColumnOrder(
              formatColumns(currentSchema, columnFormatHints, access.locale),
              columnOrder
            ),
          },
        },
      ]}
    />
  )
}

export default SnapshotTableContent
